import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import type { LeaderboardPeriod } from "@/types/leaderboard"

/**
 * OKS-51 — Time-filter switch.
 *
 * Plain shadcn `Tabs` / `TabsList` / `TabsTrigger` — NOT a form (this
 * is a read-only screen; Crew hard rule §"NO shadcn `Form`" doesn't
 * apply because we're not using `Form`). Three segments left→right:
 * `This Month` (default) · `This Quarter` · `All time`.
 *
 * # Filter state in URL (TRD KD-2)
 *
 * Tab change calls `router.visit(routeHelper({ period: next }))` with
 * `only: ["rankings", "self_rank", "period", "brand_member_count",
 * "empty_kind"]` — Inertia partial reload, no full-page nav, no
 * `localStorage`/`sessionStorage`. The new URL is shareable +
 * survives a hard refresh.
 *
 * # Keyboard / a11y
 *
 * Radix Tabs ships ←/→ navigation and Enter/Space activation
 * built-in. Active segment carries `aria-selected="true"`. No extra
 * wiring needed (UX Spec §6).
 */
interface LeaderboardTabsProps {
  /** Current active period (server-resolved, NOT client state). */
  period: LeaderboardPeriod
  /**
   * Route helper that builds the URL for the given period — pass
   * either `creatorLeaderboardPath` or `crewLeaderboardPath` (both
   * accept `{period}` query option).
   */
  routeHelper: (options: { period: LeaderboardPeriod }) => string
}

const ORDER: LeaderboardPeriod[] = ["month", "quarter", "all"]

const PARTIAL_RELOAD_PROPS = [
  "rankings",
  "self_rank",
  "period",
  "brand_member_count",
  "empty_kind",
]

export function LeaderboardTabs({ period, routeHelper }: LeaderboardTabsProps) {
  const { t } = useTranslation()

  function handleChange(next: string) {
    const nextPeriod = next as LeaderboardPeriod
    if (nextPeriod === period) return

    router.visit(routeHelper({ period: nextPeriod }), {
      only: PARTIAL_RELOAD_PROPS,
      preserveState: true,
      preserveScroll: true,
    })
  }

  return (
    <Tabs
      value={period}
      onValueChange={handleChange}
      data-testid="leaderboard-tabs"
      className="w-full"
    >
      {/*
        OKS-51 — Tap-target ≥ 44 × 44 px (WCAG 2.5.5; UX Spec §5 / §6 lock).

        shadcn `TabsList` defaults to `h-9` (36 px) and `TabsTrigger`
        carries `h-[calc(100%-1px)]` — so the trigger ends up ~35 px
        with default padding, dropping below WCAG's 44 px minimum on
        touch surfaces (CI Playwright `Tap target ≥ 44 × 44 px on
        each filter segment (UX §5)` saw 29 px after the inner pad).

        We override LOCALLY rather than in `app/frontend/components/ui/tabs.tsx`
        (a ledgered edit is permitted with parity/a11y coverage — see
        `docs/frontend/README.md` rule 6 — but a local override is cheaper
        here). `h-12` on the list
        (48 px) gives the trigger its `calc(100% - 1px)` = 47 px —
        comfortably ≥ 44 across all viewports (320 → desktop). Belt-
        and-braces, we also stamp `!h-11` on each trigger so a future
        TabsList refactor that drops the explicit height still keeps
        the trigger at 44 px (and the `!` is necessary to win over
        the upstream `h-[calc(100%-1px)]`).
      */}
      <TabsList className="grid h-12 w-full grid-cols-3 p-1">
        {ORDER.map((value) => (
          <TabsTrigger
            key={value}
            value={value}
            data-testid={`leaderboard-tab-${value}`}
            className="!h-11 py-2"
          >
            {t(`leaderboard.tabs.${value}`)}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}
